import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { api } from '../api/client'
import type { Rahmen, Schrank } from '../api/typen'
import AppRahmen from '../komponenten/AppRahmen'
import Kamera from '../komponenten/Kamera'
import RahmenWaehler from '../komponenten/RahmenWaehler'

type Schritt = 'angaben' | 'gesamtfoto' | 'rahmen'

/**
 * Schritt 2: Schrank anlegen, Gesamtfoto aufnehmen (P2-02) und - falls mehrere Module im Bild
 * sind - das gemeinte Modul eingrenzen (P2-03). Die Fachzahl ist die Sollvorgabe fuer die
 * spaetere Vollstaendigkeitspruefung (P2-04).
 */
export default function SchrankNeu() {
  const { vpId } = useParams()
  const navigiere = useNavigate()

  const [schritt, setSchritt] = useState<Schritt>('angaben')
  const [bezeichnung, setBezeichnung] = useState('')
  const [notiz, setNotiz] = useState('')
  const [faecher, setFaecher] = useState(10)
  const [schrank, setSchrank] = useState<Schrank | null>(null)
  const [vorschau, setVorschau] = useState<string | null>(null)
  const [rahmen, setRahmen] = useState<Rahmen>({ x: 18, y: 10, breite: 64, hoehe: 80 })

  const anlegen = useMutation({
    mutationFn: () =>
      api.post<Schrank>(`/api/versorgungspunkte/${vpId}/schraenke`, {
        bezeichnung: bezeichnung.trim() === '' ? null : bezeichnung,
        notiz: notiz.trim() === '' ? null : notiz,
        anzahlFaecherSoll: faecher,
      }),
    onSuccess: (neuer) => {
      setSchrank(neuer)
      setSchritt('gesamtfoto')
    },
  })

  const fotoHochladen = useMutation({
    mutationFn: (bild: Blob) =>
      api.bild<Schrank>(`/api/schraenke/${schrank!.id}/gesamtfoto`, bild, 'gesamt.jpg'),
    onSuccess: () => setSchritt('rahmen'),
  })

  const rahmenSpeichern = useMutation({
    mutationFn: () => api.patch<Schrank>(`/api/schraenke/${schrank!.id}`, { rahmen }),
    onSuccess: () => navigiere(`/schraenke/${schrank!.id}`),
  })

  if (schritt === 'angaben') {
    return (
      <AppRahmen
        titel="Schrank anlegen"
        unter="Schritt 2 von 3"
        zurueck={`/versorgungspunkte/${vpId}`}
        fuss={
          <button className="btn btn-primaer" disabled={anlegen.isPending} onClick={() => anlegen.mutate()}>
            Weiter zum Gesamtfoto
          </button>
        }
      >
        <div className="feld">
          <label className="label" htmlFor="bezeichnung">
            Bezeichnung (optional)
          </label>
          <input
            id="bezeichnung"
            type="text"
            placeholder="wird sonst fortlaufend vergeben"
            value={bezeichnung}
            onChange={(ereignis) => setBezeichnung(ereignis.target.value)}
          />
        </div>

        <div className="feld">
          <label className="label" htmlFor="notiz">
            Notiz (optional)
          </label>
          <textarea
            id="notiz"
            placeholder="Besonderheiten dieses Schranks, Hinweise für die Nachbearbeitung"
            value={notiz}
            onChange={(ereignis) => setNotiz(ereignis.target.value)}
          />
        </div>

        <div className="gruppe">
          <span className="label">Anzahl Fächer</span>
          <span className="sek">Daraus ergibt sich die Sollzahl der Fotos: zwei je Fach.</span>
          <div className="stepper">
            <button onClick={() => setFaecher((wert) => Math.max(1, wert - 1))} aria-label="weniger">
              −
            </button>
            <span className="wert">{faecher}</span>
            <button onClick={() => setFaecher((wert) => Math.min(30, wert + 1))} aria-label="mehr">
              +
            </button>
          </div>
        </div>

        {anlegen.isError && <p className="hinweiszeile">{(anlegen.error as Error).message}</p>}
      </AppRahmen>
    )
  }

  if (schritt === 'gesamtfoto') {
    return (
      <AppRahmen
        titel={schrank?.bezeichnung ?? 'Schrank'}
        unter="Gesamtfoto aufnehmen"
        zurueck={() => setSchritt('angaben')}
        fuss={
          <button className="btn-dezent" onClick={() => navigiere(`/schraenke/${schrank!.id}`)}>
            Ohne Gesamtfoto weiter
          </button>
        }
      >
        <p className="sek">
          Das Gesamtfoto dient der Zuordnung und Nachvollziehbarkeit, nicht der Analyse. Den ganzen Schrank
          ins Bild nehmen.
        </p>
        <Kamera
          gesperrt={fotoHochladen.isPending}
          hinweis={fotoHochladen.isPending ? 'Wird gespeichert …' : undefined}
          beiAufnahme={(bild) => {
            setVorschau(URL.createObjectURL(bild))
            fotoHochladen.mutate(bild)
          }}
        />
      </AppRahmen>
    )
  }

  return (
    <AppRahmen
      titel={schrank?.bezeichnung ?? 'Schrank'}
      unter="Modul eingrenzen"
      zurueck={() => setSchritt('gesamtfoto')}
      fuss={
        <>
          <button
            className="btn btn-primaer"
            disabled={rahmenSpeichern.isPending}
            onClick={() => rahmenSpeichern.mutate()}
          >
            Eingrenzung übernehmen
          </button>
          <button className="btn-dezent" onClick={() => navigiere(`/schraenke/${schrank!.id}`)}>
            Ganzer Schrank, keine Eingrenzung
          </button>
        </>
      }
    >
      <p className="sek">
        Sind mehrere Module im Bild, den Rahmen auf das gemeinte Modul ziehen. Ecke unten rechts zieht die
        Größe.
      </p>
      {vorschau && <RahmenWaehler bildUrl={vorschau} rahmen={rahmen} aendern={setRahmen} />}
    </AppRahmen>
  )
}
